import { StorageProvider } from "src/providers";

interface BootstrapSelectEntry {
	value: string;
	text: string;
	selectedText?: string;
	group?: string;
}

interface SelectPickerOptions extends Partial<BootstrapSelectOptions> {
	interval?: boolean;
	localStorage?: string | boolean | StorageProvider<string>;
}

const selectPickerOptionsDefaults: SelectPickerOptions = {
	interval: false,
	localStorage: false,
}

export class SelectPicker {
	className: string;
	selectElement: JQuery<HTMLSelectElement>;
	buttonElement: JQuery<HTMLButtonElement>;
	valueElement: JQuery<HTMLInputElement> | undefined;
	entries: Map<string, BootstrapSelectEntry> = new Map();

	private stopChangeEvent: boolean = false;
	private options: SelectPickerOptions;
	private storage: StorageProvider<string> | undefined;

	public onChangeHandler: ((e: JQuery.Event, clickedIndex: number | null, isSelected: boolean | null, previousValue: string) => void) | null = null;

	selected(): BootstrapSelectEntry[] {
		const values = this.values;
		if (this.options.interval && values.length != 0) {
			let selectedOptionElement = this.selectElement.find(`option[value="${values[0]}`).prev();
			while (selectedOptionElement.length > 0) {
				values.push(selectedOptionElement.attr('value') ?? '');
				selectedOptionElement = selectedOptionElement.prev();
			}
		}
		return <BootstrapSelectEntry[]>this.values.map(s => this.entries.get(s)).filter(i => i);
	}


	private set values(value: BootstrapSelectEntry[] | string[]) {
		const selectValues: string[] = value.length == 0 ? [] : typeof value[0] === "string" ? <any>value : (<BootstrapSelectEntry[]>value).map(e => e.value);
		this.selectElement.val(selectValues);
		this.saveValues(selectValues);
		this.refresh();
		this.selectElement.trigger('changed.bs.select');
	}

	public get values(): string[] {
		const selectedValues = this.selectElement.val() ?? '';
		const values = typeof selectedValues === "string" ? [selectedValues] : selectedValues;
		return values.filter(s => s);
	}

	constructor(selector: string | HTMLElement, className?: string, opts?: SelectPickerOptions) {
		this.className = className || '';
		this.options = { ...selectPickerOptionsDefaults, ...opts };
		this.options.maxOptions = this.options.interval ? 1 : this.options.maxOptions;

		if (typeof selector === 'string') {
			this.selectElement = $(selector);
		} else if (selector instanceof HTMLSelectElement) {
			this.selectElement = $(selector);
		} else {
			throw new Error('Dropdown selector must be a string or HTMLSelectElement');
		}
		this.valueElement = $(`input[data-selectpicker="#${this.selectElement.attr('id')}"]`);
		this.selectElement.selectpicker(opts);
		this.buttonElement = this.selectElement.siblings('.dropdown-toggle');

		if (this.options.localStorage) {
			this.storage = this.options.localStorage instanceof Object
				? this.options.localStorage
				: new StorageProvider<string>(typeof this.options.localStorage === "string" ? this.options.localStorage : this.selectElement.attr('id')!);
		}

		this.selectElement.on('changed.bs.select', (e, clickedIndex, isSelected, previousValue) => {
			this.saveValues(this.values);
			this.render();
			if (!this.stopChangeEvent) {
				this.onChangeHandler?.call(this, e, clickedIndex, isSelected, previousValue);
			}
		});
	}

	public deselectEntry(value: string): SelectPicker {
		this.values = this.values.filter(s => s !== value);
		return this;
	}

	public setEntries(entries: string[] | BootstrapSelectEntry[], selectedValues?: string | string[] | undefined): SelectPicker {
		this.stopChangeEvent = true;
		if (typeof selectedValues === "undefined") {
			selectedValues = this.loadValues() ?? this.values;
		}
		if (typeof selectedValues === 'string') {
			selectedValues = [selectedValues];
		}
		if (typeof entries[0] === 'string') {
			entries = (entries as string[]).map(o => ({ value: o, text: o }));
		}
		this.selectElement.empty();
		(entries as BootstrapSelectEntry[])
			.sort((a, b) => a.text > b.text ? 1 : a.text < b.text ? -1 : 0)
			.forEach(opt => this.addOption(opt, selectedValues ? selectedValues.includes(opt.value) : false));
		this.refresh();
		this.render();
		this.stopChangeEvent = false;
		return this;
	}

	private addOption(opt: BootstrapSelectEntry, selected: boolean = false): JQuery {
		this.entries.set(opt.value, opt);
		const option = $('<option></option>')
			.attr('value', opt.value)
			.text(opt.text);
		if (selected) {
			option.prop('selected', true);
		}
		if (this.options.interval && typeof opt.group === "undefined") {
			opt.group = '';
		}
		const parentElement = typeof opt.group === "undefined" ? this.selectElement : this.getOrAddOptionGroup(opt.group);
		return option.appendTo(parentElement);
	}

	private getOrAddOptionGroup(label: string): JQuery {
		const optGroupElement = this.selectElement.find(`optgroup[label="${label}"]`);
		if (optGroupElement.length == 1) return optGroupElement;
		const maxOptions = this.options.interval ? ' data-max-options="1"' : '';
		return $(`<optgroup label="${label}"${maxOptions} />`).appendTo(this.selectElement);
	}

	private refresh(): SelectPicker {
		this.selectElement.selectpicker('refresh');
		return this;
	}

	private render(): void {
		const selectedContainer = this.buttonElement.find('.filter-option-inner-inner');

		selectedContainer.toggleClass('filter-pill-holder', true);
		const selected = this.selected();
		if (selected.length === 0) return;

		selectedContainer.empty();
		if (this.options.interval) {
			const entry = this.entries.values().next().value;
			const pill = this.renderPill(entry!, 'disabled');
			selectedContainer.append(pill, ' – ');
		}
		selected.forEach(s => {
			const pill = this.renderPill(s);
			selectedContainer.append(pill);
		});
	}

	private renderPill(entry: BootstrapSelectEntry, className: 'disabled' | '' = ''): JQuery<HTMLElement> {
		if (!entry) return $();
		const pill = document.createElement('span');
		pill.className = `filter-pill ${className ? className : this.className}`;
		pill.setAttribute('data-value', entry.value);
		pill.setAttribute('data-type', this.className);
		pill.textContent = entry.selectedText ?? entry.text;

		pill.addEventListener('click', (ev) => {
			ev.stopPropagation();
			this.deselectEntry(entry.value);
		});
		return $(pill);
	}

	private saveValues(values: string[]) {
		if (this.storage) {
			this.storage.value = values.join(',');
		}
		this.valueElement?.val(values.join(','));
	}

	private loadValues(): string[] | undefined {
		let value: any = this.storage?.value;
		if (value) return value.split(',');
		value ??= <string | undefined>this.valueElement?.val();
		return (typeof value !== "undefined") ? value.split(',') : undefined;
	}
}

export namespace SelectPicker {
	export const Null = null! as SelectPicker;
}